import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { motion } from "framer-motion";
import { FiUserPlus } from "react-icons/fi";
import { CgSpinner } from "react-icons/cg";
import { TextField, MenuItem } from "@mui/material";

// ==================== Validation Schema ====================
const validationSchema = Yup.object({
  firstName: Yup.string()
    .max(15, "Must be 15 characters or less")
    .required("First Name is required"),
  lastName: Yup.string()
    .max(20, "Must be 20 characters or less")
    .required("Last Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

// ==================== Animation Variants ====================
const fadeInUp = {
  hidden: { opacity: 0, y: 5 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const getTextFieldSx = (theme) => {
  const cardBg = theme === "dark" ? "#1f2937" : "#ffffff";

  return {
    "& .MuiInputLabel-root": {
      color: theme === "dark" ? "#9ca3af" : "#6b7280",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#3b82f6",
    },
    // ✅ Only set background — no padding, no marginLeft, no !important
    "& .MuiInputLabel-shrink": {
      backgroundColor: cardBg,
      paddingLeft: "4px",
      paddingRight: "4px",
    },

    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme === "dark" ? "#374151" : "#f9fafb",
      color: theme === "dark" ? "#f3f4f6" : "#111827",
      "& fieldset": {
        borderColor: theme === "dark" ? "#4b5563" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#3b82f6",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3b82f6",
        borderWidth: "2px",
      },
    },

    "& .MuiFormHelperText-root": {
      fontSize: "0.7rem",
      lineHeight: 1.2,
      marginTop: "2px",
      marginLeft: "4px",
      color: theme === "dark" ? "#ef4444" : undefined,
    },
  };
};

// ==================== Themed TextField Component ====================
const ThemedTextField = ({
  theme,
  startAdornment,
  endAdornment,
  children,
  ...props
}) => (
  <motion.div whileFocus={{ scale: 1.01 }} className="relative">
    <TextField
      fullWidth
      variant="outlined"
      size="small"
      InputProps={{
        className: theme === "dark" ? "text-gray-100" : "",
        ...(startAdornment && { startAdornment }),
        ...(endAdornment && { endAdornment }),
      }}
      SelectProps={{
        MenuProps: {
          PaperProps: {
            sx: {
              backgroundColor: theme === "dark" ? "#374151" : "#ffffff",
              color: theme === "dark" ? "#f3f4f6" : "#111827",
              "& .MuiMenuItem-root": {
                "&:hover": {
                  backgroundColor: theme === "dark" ? "#4b5563" : "#f3f4f6",
                },
                "&.Mui-selected": {
                  backgroundColor: theme === "dark" ? "#1e3a5f" : "#e0e7ff",
                  "&:hover": {
                    backgroundColor: theme === "dark" ? "#1e3a5f" : "#c7d2fe",
                  },
                },
              },
            },
          },
        },
      }}
      sx={getTextFieldSx(theme)}
      {...props}
    >
      {children}
    </TextField>
  </motion.div>
);

// ==================== Form Field Components ====================
const NameAndGenderFields = ({
  errors,
  values,
  touched,
  handleChange,
  handleBlur,
  theme,
}) => (
  <motion.div
    variants={fadeInUp}
    className="grid grid-cols-1 md:grid-cols-3 gap-3"
  >
    <ThemedTextField
      label="First Name"
      name="firstName"
      value={values.firstName}
      onChange={handleChange}
      onBlur={handleBlur}
      error={touched.firstName && Boolean(errors.firstName)}
      helperText={touched.firstName && errors.firstName}
      theme={theme}
    />

    <ThemedTextField
      label="Last Name"
      name="lastName"
      value={values.lastName}
      onChange={handleChange}
      onBlur={handleBlur}
      error={touched.lastName && Boolean(errors.lastName)}
      helperText={touched.lastName && errors.lastName}
      theme={theme}
    />

    <ThemedTextField
      select
      label="Gender"
      name="gender"
      value={values.gender}
      onChange={handleChange}
      theme={theme}
    >
      <MenuItem value="male">Male</MenuItem>
      <MenuItem value="female">Female</MenuItem>
      <MenuItem value="other">Other</MenuItem>
      <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
    </ThemedTextField>
  </motion.div>
);

const EmailField = ({
  errors,
  values,
  touched,
  handleChange,
  handleBlur,
  theme,
}) => (
  <motion.div variants={fadeInUp}>
    <ThemedTextField
      label="Email Address"
      name="email"
      type="email"
      value={values.email}
      onChange={handleChange}
      onBlur={handleBlur}
      error={touched.email && Boolean(errors.email)}
      helperText={touched.email && errors.email}
      theme={theme}
    />
  </motion.div>
);

const PasswordFieldComponent = ({
  name,
  label,
  showPassword,
  toggleVisibility,
  errors,
  values,
  touched,
  handleChange,
  handleBlur,
  theme,
}) => (
  <motion.div variants={fadeInUp}>
    <ThemedTextField
      label={label}
      name={name}
      type={showPassword ? "text" : "password"}
      value={values[name]}
      onChange={handleChange}
      onBlur={handleBlur}
      error={touched[name] && Boolean(errors[name])}
      helperText={touched[name] && errors[name]}
      theme={theme}
      endAdornment={
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          type="button"
          onClick={toggleVisibility}
          className={`p-1 ${
            theme === "dark"
              ? "text-gray-400 hover:text-blue-300"
              : "text-gray-400 hover:text-blue-600"
          }`}
        >
          {showPassword ? <FaEyeSlash /> : <FaEye />}
        </motion.button>
      }
    />
  </motion.div>
);

const SubmitButton = ({ isValid, dirty, loading }) => (
  <motion.button
    variants={fadeInUp}
    type="submit"
    disabled={!(isValid && dirty) || loading}
    whileHover={{ scale: !(isValid && dirty) ? 1 : 1.02 }}
    whileTap={{ scale: !(isValid && dirty) ? 1 : 0.98 }}
    className={`w-full py-3 rounded-xl font-semibold text-base transition-all flex items-center justify-center gap-3 mt-2 ${
      isValid && dirty
        ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl"
        : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
    }`}
  >
    {loading ? (
      <>
        <CgSpinner className="animate-spin text-xl" />
        <span>Creating Account...</span>
      </>
    ) : (
      <>
        <div className="p-1.5 bg-white/20 rounded-lg">
          <FiUserPlus className="text-lg" />
        </div>
        <span>Create Account</span>
      </>
    )}
  </motion.button>
);

// ==================== Main Component ====================
const SignupForm = ({ handleSignup, theme }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const initialValues = {
    firstName: "",
    lastName: "",
    gender: "male",
    email: "",
    password: "",
    confirmPassword: "",
  };

  const onSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      await handleSignup(values);
    } catch (error) {
      console.error("Signup error:", error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize={false}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        errors,
        values,
        touched,
        handleBlur,
        handleChange,
        isValid,
        dirty,
      }) => (
        <Form className="mt-4">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerChildren}
            className="space-y-3 max-h-[55vh] overflow-y-auto pr-1"
          >
            <NameAndGenderFields
              errors={errors}
              values={values}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              theme={theme}
            />

            <EmailField
              errors={errors}
              values={values}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              theme={theme}
            />

            <PasswordFieldComponent
              name="password"
              label="Password"
              showPassword={showPassword}
              toggleVisibility={() => setShowPassword((prev) => !prev)}
              errors={errors}
              values={values}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              theme={theme}
            />

            <PasswordFieldComponent
              name="confirmPassword"
              label="Confirm Password"
              showPassword={showConfirmPassword}
              toggleVisibility={() => setShowConfirmPassword((prev) => !prev)}
              errors={errors}
              values={values}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              theme={theme}
            />

            <SubmitButton isValid={isValid} dirty={dirty} loading={loading} />
          </motion.div>
        </Form>
      )}
    </Formik>
  );
};

export default SignupForm;